import { ChevronUp } from "lucide-react";
import { ButtonDropdownComboBase, type ButtonDropdownComboBaseProps } from "@core/ui";
import { Button } from "../Button";
import { Dropdown } from "../Dropdown";

export type Props = Omit<
  ButtonDropdownComboBaseProps,
  "renderButton" | "renderDropdownTrigger" | "renderDropdown"
> & {
  /** Icon component for the primary button */
  buttonIcon: React.ComponentType<{ className?: string }>;
  /** Whether the action is destructive */
  destructive?: boolean;
};

/**
 * Styled button with dropdown combo component wrapping ButtonDropdownComboBase.
 * Combines a primary action button with a dropdown for additional options.
 */
export const ButtonDropdownCombo = ({
  buttonIcon,
  buttonLabel,
  buttonOnClick,
  dropdownOptions,
  destructive,
  disabled = false,
  className,
  ...props
}: Props) => {
  return (
    <ButtonDropdownComboBase
      buttonLabel={buttonLabel}
      buttonOnClick={buttonOnClick}
      dropdownOptions={dropdownOptions}
      disabled={disabled}
      className={`flex items-center ${className || ""}`}
      renderButton={({ label, onClick, disabled: isDisabled }) => (
        <Button
          iconComponent={buttonIcon}
          onClick={onClick}
          variant="ghost"
          theme="dark"
          destructive={destructive}
          disabled={isDisabled}
        >
          {label}
        </Button>
      )}
      renderDropdownTrigger={({ disabled: isDisabled }) => (
        <Button
          iconComponent={ChevronUp}
          variant="ghost"
          theme="dark"
          disabled={isDisabled}
        />
      )}
      renderDropdown={({ trigger, children }) => (
        <Dropdown trigger={trigger}>
          <div className="flex flex-col max-h-[600px] overflow-y-auto">
            {children}
          </div>
        </Dropdown>
      )}
      data-testid="button-dropdown-combo"
      {...props}
    />
  );
};

export default ButtonDropdownCombo;

